/**
 * Verificación de permisos del usuario en sesión.
 *
 * ## Cómo funciona ahora
 *
 * El backend manda el **nombre** de cada permiso junto con su id
 * (`/auth/login` y `/auth/me` → `permissionsActives: [{ permission_id, name }]`),
 * así que la comparación es directa contra la base. Agregar un permiso nuevo ya
 * no requiere tocar este archivo.
 *
 * ## Por qué cambió
 *
 * Antes aquí vivía un diccionario UUID→nombre escrito a mano, y era la única
 * forma de traducir lo que mandaba el backend. Se desincronizaba en silencio: al
 * contrastarlo contra la tabla `permissions` se encontró que el UUID anotado para
 * `CL_CREATE` (`d42a88f2-…`) **no existe en la base** — el real es
 * `60656207-4add-…`. Efecto: `hasPermission(p, 'CL_CREATE')` devolvía `false`
 * para todo el mundo, incluido el Master, y el botón de crear cliente
 * simplemente no aparecía. No había error, log ni pista: el permiso no se
 * "negaba", nunca se encontraba.
 *
 * Ese es el modo de falla que este cambio elimina.
 */

/**
 * Diccionario de respaldo, SOLO para la ventana de despliegue.
 *
 * El frontend y el backend se despliegan por separado, así que puede haber unos
 * minutos con frontend nuevo y backend viejo, donde `name` todavía no viaja. En
 * ese caso se cae a traducir por id.
 *
 * ⚠️ No agregar permisos nuevos aquí: el camino correcto es el nombre. Esta tabla
 * se puede borrar una vez que ambos lados estén desplegados. Los ids están
 * verificados contra la tabla `permissions` (incluido el de `CL_CREATE`, que
 * estaba mal).
 */
const legacyPermissionMap: { [key: string]: string } = {
    'd42a7efa-8268-11ef-ba97-06f8ae44a30f': 'LOADS',
    'd42a81fe-8268-11ef-ba97-06f8ae44a30f': 'LD_VIEW',
    'd42a8306-8268-11ef-ba97-06f8ae44a30f': 'LD_AD_VIEW',
    'd42a8392-8268-11ef-ba97-06f8ae44a30f': 'LD_EDIT',
    'd42a8402-8268-11ef-ba97-06f8ae44a30f': 'LD_AD_EDIT',
    'd42a8470-8268-11ef-ba97-06f8ae44a30f': 'LD_CREATE',
    'a6840968-efc6-11f0-9fc4-06f8ae44a30f': 'LD_DELETE_DOCUMENTS',
    'b18ac906-efc6-11f0-9fc4-06f8ae44a30f': 'LD_DELETE_NOTES',
    'd42a84f0-8268-11ef-ba97-06f8ae44a30f': 'USER',
    'd42a857d-8268-11ef-ba97-06f8ae44a30f': 'US_VIEW',
    'd42a85e9-8268-11ef-ba97-06f8ae44a30f': 'US_AD_VIEW',
    'd42a865a-8268-11ef-ba97-06f8ae44a30f': 'US_EDIT',
    'd42a86c9-8268-11ef-ba97-06f8ae44a30f': 'US_AD_EDIT',
    'd42a8736-8268-11ef-ba97-06f8ae44a30f': 'US_CREATE',
    '8cc348e7-f0b5-11f0-9fc4-06f8ae44a30f': 'US_HAVE_ALL_CLIENTS',
    'd42a879f-8268-11ef-ba97-06f8ae44a30f': 'CLIENTS',
    'd42a880b-8268-11ef-ba97-06f8ae44a30f': 'CL_VIEW',
    'd42a887d-8268-11ef-ba97-06f8ae44a30f': 'CL_EDIT',
    '60656207-4add-11f0-ba97-06f8ae44a30f': 'CL_CREATE',
    'd42a8965-8268-11ef-ba97-06f8ae44a30f': 'DELIVERIES',
    'd42a89e4-8268-11ef-ba97-06f8ae44a30f': 'DL_VIEW',
    'd42a8d84-8268-11ef-ba97-06f8ae44a30f': 'DL_EDIT',
    'd42a8e27-8268-11ef-ba97-06f8ae44a30f': 'DL_CREATE',
    '76d7e1c0-feac-11ef-ba97-06f8ae44a30f': 'TRACKING',
    '76d8026e-feac-11ef-ba97-06f8ae44a30f': 'TR_VIEW',
    '76d807d6-feac-11ef-ba97-06f8ae44a30f': 'TR_EDIT',
    '299b9094-4ade-11f0-ba97-06f8ae44a30f': 'LD_ADD',
    '9275dce1-52a5-11f0-ba97-06f8ae44a30f': 'RATER',
    '5c6cd9b1-55e3-11f0-9fc4-06f8ae44a30f': 'LD_ADD_NOTES',
    '4cf06a9e-55e3-11f0-9fc4-06f8ae44a30f': 'LD_VIEW_NOTES',
    '8ef45904-fb9e-11f0-9fc4-06f8ae44a30f': 'LD_HISTORY',
    'd42a8e27-8268-11ef-ba97-06fffe44f30f': 'TR_UPDATE_LOCATION',
    'd42a8e27-8278-11ef-ba97-06fffe44f30f': 'LD_DELETE',
    'd42a8e27-8268-27ef-ba97-06fffe44f30f': 'CATALOGS',
    'd42a8e27-8268-27ef-ba97-06fffe44f27f': 'CT_VIEW',
    'd42a8e27-2727-27ef-ba97-06fffe44f30f': 'CT_CREATE',
    'd27a8e27-8268-27ef-ba97-00fffe44f30f': 'CT_EDIT',
    'd42a8e27-8268-27ef-ba27-06fffe44f27f': 'CT_DELETE'
};

/** Lo que manda el backend por cada permiso asignado al usuario. */
export interface ActivePermission {
    permission_id: string;
    /** Nombre del permiso. Opcional solo por la ventana de despliegue. */
    name?: string | null;
}

const hasPermission = (
    userPermissions: ActivePermission[] | undefined | null,
    permission: string
): boolean => {
    if (!userPermissions || !userPermissions.length) {
        return false;
    }

    return userPermissions.some((userPermission) => {
        // Camino normal: el backend ya mandó el nombre.
        if (userPermission?.name) {
            return userPermission.name === permission;
        }
        // Respaldo: backend viejo, se traduce el id.
        return legacyPermissionMap[userPermission?.permission_id] === permission;
    });
};

export default hasPermission;
